import { type FC, type KeyboardEvent, type ReactNode, useCallback, useMemo } from "react";
import { Link, generatePath, useLocation, useNavigate } from "react-router-dom";

import { Avatar, Box, Button, Card, Center, Flex, Skeleton, Table, Text } from "@mantine/core";

import { AbsoluteRoute } from "../../core/routes";
import { InsightsRoundButton } from "../../lenses/components/list/row/InsightsRoundButton";
import { truncate } from "../../shared/styles";
import { type TopLensRow } from "../hooks/useMergeTopLensesWithInfo";

import {
    clickableRow,
    insightsCell,
    sectionHeader,
    sectionSubtitle,
    seeAllButton,
    seeAllLabel,
    tableHeader,
} from "./HomeTopLensesSection.module.css";
import { NoDataForTimeRange, SeeAll, TableHeaderIcon, TableHeaderName } from "./messages.generated";

const SKELETON_ROW_COUNT = 5;
const AVATAR_SIZE = 40;
// paragraph-p300-lineheight
const TEXT_LINE_HEIGHT = 14;

const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
    <Table.Tr key={index}>
        <Table.Td>
            <Skeleton height={AVATAR_SIZE} width={AVATAR_SIZE} circle />
        </Table.Td>
        <Table.Td>
            <Skeleton height={TEXT_LINE_HEIGHT} width="80%" />
        </Table.Td>
        <Table.Td>
            <Skeleton height={TEXT_LINE_HEIGHT} width={50} />
        </Table.Td>
    </Table.Tr>
));

interface HomeTopLensesSectionProps {
    title: ReactNode;
    subtitle: ReactNode;
    valueColumnLabel: ReactNode;
    data: TopLensRow[];
    loading?: boolean;
    seeAllHref: string;
    insightsTab?: string;
}

export const HomeTopLensesSection: FC<HomeTopLensesSectionProps> = ({
    title,
    subtitle,
    valueColumnLabel,
    data,
    loading = false,
    seeAllHref,
    insightsTab,
}) => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const seeAllHrefWithSearch = `${seeAllHref}${search}`;

    const handleRowClick = useCallback(
        (lens: TopLensRow) => {
            if (lens.orgId && lens.lensFolderId) {
                navigate(
                    generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENS, {
                        orgId: lens.orgId,
                        lensFolderId: lens.lensFolderId,
                        lensId: lens.id,
                    }),
                );
            } else {
                navigate(generatePath(AbsoluteRoute.LENS, { lensId: lens.id }));
            }
        },
        [navigate],
    );

    const handleRowKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTableRowElement>, lens: TopLensRow) => {
            if (event.key !== "Enter" && event.key !== " ") {
                return;
            }

            event.preventDefault();
            handleRowClick(lens);
        },
        [handleRowClick],
    );

    const rows = useMemo(
        () =>
            data.map((lens) => (
                <Table.Tr
                    key={lens.id}
                    className={clickableRow}
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                        handleRowClick(lens);
                    }}
                    onKeyDown={(event) => {
                        handleRowKeyDown(event, lens);
                    }}
                >
                    <Table.Td>
                        <Avatar src={lens.icon} size={AVATAR_SIZE} radius={0} />
                    </Table.Td>
                    <Table.Td className="paragraph-p300">
                        <div className={truncate}>{lens.name}</div>
                    </Table.Td>
                    <Table.Td className="paragraph-p300">{lens.value}</Table.Td>
                    <Table.Td
                        className={insightsCell}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <InsightsRoundButton
                            lensId={lens.id}
                            orgId={lens.orgId}
                            lensFolderId={lens.lensFolderId}
                            insightsTab={insightsTab}
                            search={search}
                            isDisabled={false}
                        />
                    </Table.Td>
                </Table.Tr>
            )),
        [data, handleRowClick, handleRowKeyDown, insightsTab, search],
    );

    return (
        <Box>
            <Flex className={sectionHeader} justify="space-between" align="center">
                <div>
                    <h3 className="heading-h400">{title}</h3>
                    <p className={sectionSubtitle}>{subtitle}</p>
                </div>
                <Button
                    component={Link}
                    to={seeAllHrefWithSearch}
                    variant="white"
                    size="sm"
                    radius="xl"
                    className={seeAllButton}
                >
                    <span className={seeAllLabel}>
                        <SeeAll />
                    </span>
                </Button>
            </Flex>
            <Card shadow="sm" radius={0} p={5} mih={300}>
                <Table verticalSpacing={5} withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th className={tableHeader} w={60}>
                                <TableHeaderIcon />
                            </Table.Th>
                            <Table.Th className={tableHeader}>
                                <TableHeaderName />
                            </Table.Th>
                            <Table.Th className={tableHeader} w={70}>
                                {valueColumnLabel}
                            </Table.Th>
                            <Table.Th w={40} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{loading ? skeletonRows : rows}</Table.Tbody>
                </Table>
                {!loading && data.length === 0 && (
                    <Center flex={1}>
                        <Text className="paragraph-p200" c="dimmed">
                            <NoDataForTimeRange />
                        </Text>
                    </Center>
                )}
            </Card>
        </Box>
    );
};
